import { useParams, Link } from 'react-router-dom';
import { ArrowLeftIcon } from '@heroicons/react/24/outline';

import { Button, Card, Spinner } from '@realvisor/ui';
import { useForm, type FormField } from '@/hooks';

function FormFieldPreview({ field }: { field: FormField }) {
  const baseClasses = "w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-primary-500 focus:ring-primary-500";
  
  switch (field.type) {
    case 'textarea':
      return (
        <textarea
          className={baseClasses}
          placeholder={field.placeholder || ''}
          rows={3}
          disabled
        />
      );
    case 'select':
      return (
        <select className={baseClasses} disabled>
          <option value="">Vyberte...</option>
          {field.options?.map((opt) => (
            <option key={opt.value} value={opt.value}>{opt.label}</option>
          ))}
        </select>
      );
    case 'checkbox':
      return (
        <div className="flex items-center gap-2">
          <input type="checkbox" className="rounded border-gray-300" disabled />
          <span className="text-sm text-gray-600">{field.placeholder || field.label}</span>
        </div>
      );
    default:
      return (
        <input
          type={field.type === 'phone' ? 'tel' : field.type}
          className={baseClasses}
          placeholder={field.placeholder || ''}
          disabled
        />
      );
  }
}

export default function FormPreview() {
  const { id } = useParams<{ id: string }>();
  const { data: form, isLoading } = useForm(id || '');

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Spinner size="lg" />
      </div>
    );
  }

  if (!form) {
    return (
      <Card padding="lg">
        <div className="text-center py-8">
          <h3 className="text-sm font-medium text-gray-900">Formulář nenalezen</h3>
          <Link to="/forms" className="mt-2 text-sm text-primary-600 hover:text-primary-700">
            Zpět na seznam
          </Link>
        </div>
      </Card>
    );
  }

  const config = form.config;
  const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:3000';

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Link to={`/forms/${id}`}>
            <Button variant="outline" size="sm">
              <ArrowLeftIcon className="h-4 w-4" />
            </Button>
          </Link>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Náhled: {form.name}</h1>
            <p className="text-sm text-gray-500">
              Kód: <code className="bg-gray-100 px-1 rounded">{form.code}</code>
            </p>
          </div>
        </div>
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        {/* Preview */}
        <div>
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Náhled formuláře</h2>
          <div 
            className="rounded-xl shadow-lg overflow-hidden"
            style={{ 
              backgroundColor: config.theme?.backgroundColor || '#ffffff',
            }}
          >
            <div 
              className="px-6 py-4"
              style={{ backgroundColor: config.theme?.primaryColor || '#2563eb' }}
            >
              <h3 className="text-lg font-semibold text-white">
                {form.name}
              </h3>
              <p className="text-sm text-white/80">
                Vyplňte formulář a ozveme se vám
              </p>
            </div>
            
            <div className="p-6 space-y-4">
              {config.fields?.filter((field: any) => !field.honeypot && !field.hidden).map((field) => (
                <div key={field.name}>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {field.label}
                    {field.required && <span className="text-red-500 ml-1">*</span>}
                  </label>
                  <FormFieldPreview field={field} />
                </div>
              ))}
              
              <button
                className="w-full py-2.5 px-4 rounded-md text-white font-medium transition-colors"
                style={{ backgroundColor: config.theme?.primaryColor || '#2563eb' }}
                disabled
              >
                {config.submitButtonText || 'Odeslat'}
              </button>
            </div>
          </div>

          <p className="mt-4 text-sm text-gray-500 text-center">
            Úspěšná zpráva: "{config.successMessage || 'Děkujeme!'}"
          </p>
        </div>

        {/* Embed Code */}
        <div className="space-y-6">
          <Card padding="md">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Embed kód</h2>
            
            {(() => {
              const widgetUrl = import.meta.env['VITE_WIDGET_URL'] || 'https://realvisor-widget.vercel.app';
              const primaryColor = config.theme?.primaryColor || '#2563eb';
              const maxWidth = config.theme?.maxWidth || '480px';
              const shadow = config.theme?.shadow || 'lg';
              return (
                <div className="space-y-4">
                  <div>
                    <h3 className="text-sm font-medium text-gray-700 mb-2">Základní embed</h3>
                    <div className="bg-gray-900 rounded-lg p-4 overflow-x-auto">
                      <code className="text-sm text-green-400 whitespace-pre">
{`<!-- Kontejner pro formulář -->
<div id="realvisor-form"></div>

<!-- Widget script -->
<script src="${widgetUrl}/widget.iife.js"></script>
<script>
  RealvisorWidget.createValuoForm(document.getElementById('realvisor-form'), {
    apiUrl: '${apiUrl}/api/v1',
    formCode: '${form.code}',
    projectId: '${form.projectId}',
    primaryColor: '${primaryColor}',
    maxWidth: '${maxWidth}',
    shadow: '${shadow}'
  });
</script>`}
                      </code>
                    </div>
                  </div>

                  <div>
                    <h3 className="text-sm font-medium text-gray-700 mb-2">S callback funkcí</h3>
                    <div className="bg-gray-900 rounded-lg p-4 overflow-x-auto">
                      <code className="text-sm text-green-400 whitespace-pre">
{`<div id="realvisor-form"></div>
<script src="${widgetUrl}/widget.iife.js"></script>
<script>
  RealvisorWidget.createValuoForm(document.getElementById('realvisor-form'), {
    apiUrl: '${apiUrl}/api/v1',
    formCode: '${form.code}',
    projectId: '${form.projectId}',
    primaryColor: '${primaryColor}',
    maxWidth: '${maxWidth}',
    shadow: '${shadow}',
    onSuccess: (data) => {
      console.log('Formulář odeslán:', data);
      // Vaše vlastní logika
    }
  });
</script>`}
                      </code>
                    </div>
                  </div>
                  
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <h3 className="text-sm font-medium text-blue-900 mb-2">Dostupné parametry</h3>
                    <ul className="text-xs text-blue-800 space-y-1">
                      <li><code className="bg-blue-100 px-1 rounded">primaryColor</code> - hlavní barva (hex)</li>
                      <li><code className="bg-blue-100 px-1 rounded">maxWidth</code> - max. šířka ('480px', '100%')</li>
                      <li><code className="bg-blue-100 px-1 rounded">shadow</code> - stín ('none', 'sm', 'md', 'lg')</li>
                      <li><code className="bg-blue-100 px-1 rounded">borderRadius</code> - zaoblení rohů ('12px')</li>
                    </ul>
                  </div>
                </div>
              );
            })()}
          </Card>

          <Card padding="md">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">API Endpoint</h2>
            <div className="space-y-3 text-sm">
              <div>
                <span className="font-medium text-gray-700">Config:</span>
                <code className="ml-2 text-gray-600 bg-gray-100 px-2 py-1 rounded">
                  GET {apiUrl}/api/v1/public/forms/{form.code}/config
                </code>
              </div>
              <div>
                <span className="font-medium text-gray-700">Submit:</span>
                <code className="ml-2 text-gray-600 bg-gray-100 px-2 py-1 rounded">
                  POST {apiUrl}/api/v1/public/forms/{form.code}/submit
                </code>
              </div>
            </div>
          </Card>

          <Card padding="md">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Konfigurace polí</h2>
            <div className="space-y-2">
              {config.fields?.map((field, index) => (
                <div key={field.name} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-gray-400 w-5">{index + 1}.</span>
                    <span className="text-sm font-medium text-gray-700">{field.label}</span>
                    {field.required && <span className="text-xs text-red-500">*</span>}
                  </div>
                  <span className="text-xs text-gray-500 bg-gray-200 px-2 py-0.5 rounded">
                    {field.type}
                  </span>
                </div>
              ))}
            </div>
            <p className="mt-3 text-xs text-gray-500">
              Editace polí bude dostupná v další verzi
            </p>
          </Card>
        </div>
      </div>
    </div>
  );
}
